import { useMemo } from 'react';
import { useNavigate, generatePath } from 'react-router-dom';

import {
  GiftOutline,
  HomeOutline,
  MerchantOutline,
  WarningOutline,
  Profile2UserOutline,
  CardsOutline,
  UserOctagonOutline,
  BriefcaseOutline,
  ProfileCircleOutline,
  DocumentUploadOutline,
  LockOutline,
  CalendarOutline,
  SearchNormalOutline,
} from '@aslanpay/portal-common-lib';

import { globalPaths, companyPaths } from 'app/constants/routes';
import { useManagingCompanyStore, useNavigationStore } from 'app/store';

import { Command } from './types';

function useCommands(onSearchById: () => void) {
  const navigate = useNavigate();
  const view = useNavigationStore((state) => state.view);
  const company = useManagingCompanyStore((state) => state.company);

  const commands = useMemo<Command[]>(() => {
    const baseCommands: Command[] = [
      // Profile actions
      {
        id: 'my-profile',
        label: 'My Profile',
        description: 'View and edit your profile',
        icon: ProfileCircleOutline,
        category: 'navigation',
        keywords: ['profile', 'account', 'settings'],
        action: () => navigate(globalPaths.profile.path),
      },
      {
        id: 'change-password',
        label: 'Change Password',
        description: 'Update your password',
        icon: LockOutline,
        category: 'actions',
        keywords: ['password', 'security', 'auth'],
        action: () => navigate(globalPaths.changePassword.path),
      },
      {
        id: 'search-by-id',
        label: 'Search by ID',
        description: 'Find a company or individual by their ID',
        icon: SearchNormalOutline,
        category: 'actions',
        keywords: [
          'search',
          'id',
          'find',
          'lookup',
          'uuid',
          'identifier',
          'company',
          'individual',
          'employee',
        ],
        keepOpen: true,
        action: onSearchById,
      },
    ];

    // Global navigation commands
    const globalCommands: Command[] = [
      {
        id: 'dashboard',
        label: 'Dashboard',
        description: 'View global dashboard',
        icon: HomeOutline,
        category: 'navigation',
        keywords: ['home', 'dashboard', 'overview'],
        action: () => navigate(globalPaths.dashboard.path),
      },
      {
        id: 'suspicious-activity',
        label: 'Suspicious Activity',
        description: 'Monitor suspicious transactions',
        icon: WarningOutline,
        category: 'navigation',
        keywords: ['suspicious', 'fraud', 'security', 'alerts'],
        action: () => navigate(globalPaths.suspiciousActivity.path),
      },
      {
        id: 'all-companies',
        label: 'All Companies',
        description: 'View all companies',
        icon: BriefcaseOutline,
        category: 'navigation',
        keywords: ['companies', 'organizations', 'all'],
        action: () => navigate(globalPaths.companies.path),
      },
      {
        id: 'all-employees',
        label: 'All Employees',
        description: 'View all employees across companies',
        icon: Profile2UserOutline,
        category: 'navigation',
        keywords: ['employees', 'workers', 'users', 'all'],
        action: () => navigate(globalPaths.employees.path),
      },
      {
        id: 'all-payroll-reports',
        label: 'All Deduction Reports',
        description: 'View all deduction reports',
        icon: CardsOutline,
        category: 'navigation',
        keywords: ['payroll', 'reports', 'all', 'deductions'],
        action: () => navigate(globalPaths.payrollReports.path),
      },
      {
        id: 'merchants',
        label: 'Merchants',
        description: 'Manage voucher merchants',
        icon: MerchantOutline,
        category: 'navigation',
        keywords: ['merchants', 'vouchers', 'brands'],
        action: () => navigate(globalPaths.merchants.path),
      },
      {
        id: 'all-admins',
        label: 'All Admins',
        description: 'Manage admin users',
        icon: UserOctagonOutline,
        category: 'navigation',
        keywords: ['admins', 'administrators', 'all'],
        action: () => navigate(globalPaths.admins.path),
      },
      {
        id: 'approvals',
        label: 'Approvals',
        description: 'View pending approvals',
        icon: CardsOutline,
        category: 'navigation',
        keywords: ['approvals', 'pending', 'review'],
        action: () => navigate(globalPaths.approvals.path),
      },
    ];

    // Company-specific commands
    const companyCommands: Command[] = company
      ? [
          {
            id: 'company-dashboard',
            label: `${company.name} - Dashboard`,
            description: 'View company dashboard',
            icon: HomeOutline,
            category: 'navigation',
            keywords: ['dashboard', 'company', 'overview'],
            action: () =>
              navigate(generatePath(companyPaths.dashboard.path, { companyId: company.id })),
          },
          {
            id: 'company-employees',
            label: `${company.name} - Employees`,
            description: 'View company employees',
            icon: Profile2UserOutline,
            category: 'navigation',
            keywords: ['employees', 'company', 'workers'],
            action: () =>
              navigate(generatePath(companyPaths.employees.path, { companyId: company.id })),
          },
          {
            id: 'company-payroll-reports',
            label: `${company.name} - Deduction Reports`,
            description: 'View company deduction reports',
            icon: CardsOutline,
            category: 'navigation',
            keywords: ['payroll', 'reports', 'company', 'deductions'],
            action: () =>
              navigate(generatePath(companyPaths.payrollReports.path, { companyId: company.id })),
          },
          {
            id: 'company-pay-calendars',
            label: `${company.name} - Pay Calendars`,
            description: 'Manage pay calendars',
            icon: CalendarOutline,
            category: 'navigation',
            keywords: ['pay calendars', 'payroll', 'schedule'],
            action: () =>
              navigate(generatePath(companyPaths.payCalendars.path, { companyId: company.id })),
          },
          {
            id: 'company-admins',
            label: `${company.name} - Admins`,
            description: 'Manage company admins',
            icon: UserOctagonOutline,
            category: 'navigation',
            keywords: ['admins', 'company', 'administrators'],
            action: () =>
              navigate(generatePath(companyPaths.admins.path, { companyId: company.id })),
          },
          {
            id: 'company-gifting',
            label: `${company.name} - Gifting`,
            description: 'Manage company gifting',
            icon: GiftOutline,
            category: 'navigation',
            keywords: ['gifting', 'rewards', 'company'],
            action: () =>
              navigate(generatePath(companyPaths.gifting.path, { companyId: company.id })),
          },
          {
            id: 'company-uploaded-files',
            label: `${company.name} - Uploaded Files`,
            description: 'View uploaded files',
            icon: DocumentUploadOutline,
            category: 'navigation',
            keywords: ['files', 'uploads', 'documents'],
            action: () =>
              navigate(generatePath(companyPaths.uploadedFiles.path, { companyId: company.id })),
          },
        ]
      : [];

    // Combine all commands based on view
    if (view === 'company') {
      return [...baseCommands, ...companyCommands, ...globalCommands];
    }

    return [...baseCommands, ...globalCommands];
  }, [navigate, view, company, onSearchById]);

  return commands;
}

export default useCommands;
